import React from 'react'
import {useStyles, VideoProgressSlider} from "./ScoreEnrichedVideoProgressSlider.css";
import moment from "moment";
import {useIntl} from "react-intl";
import {BlurOff, BlurOn, Flag, Info} from "@mui/icons-material";
import BasicHint from "../../../../../commons/utils/hint/BasicHint";
import {SportsScore} from "@mui/icons-material";

const ScoreEnrichedVideoProgressSlider = ({
                                              scoreDefinitions,
                                              trailingPeriod,
                                              sceneStart,
                                              sceneEnd,
                                              blurringStart,
                                              blurringEnd,
                                              disabled,
                                              value,
                                              onChange
                                          }) => {

    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    const isScoreWithinRange = (s) => {
        return (s.time >= sceneStart && s.time <= sceneEnd)
    }

    const isBlurringWithinFence = () => {
        return blurringStart >= sceneStart && blurringEnd <= sceneEnd
    }

    /*
    Marks shown on the video timeline according to the definitions by the user
     */
    const getMarks = () => {
        // Remove marks, which are outside the video range
        const defs = scoreDefinitions.filter((s) => isScoreWithinRange(s))

        // Create marks
        const marks = [
            // Start / End
            {
                value: sceneStart,
                label: <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                                  hintText={<div className={classes.tooltipColumn}>
                                      <div>{moment(sceneStart * 1000).format("mm:ss:SS")}</div>
                                      <div>
                                          {intl.formatMessage({
                                              id: 'app.scoredScene.edit.videoSlider.start.label',
                                              defaultMessage: 'Start'
                                          })}
                                      </div>
                                  </div>}
                                  element={<Info className={classes.infoIcon}/>}/>
            },
            {
                value: sceneEnd,
                label: <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                                  hintText={<div className={classes.tooltipColumn}>
                                      <div>{moment(sceneEnd * 1000).format("mm:ss:SS")}</div>
                                      <div>
                                          {intl.formatMessage({
                                              id: 'app.scoredScene.edit.videoSlider.end.label',
                                              defaultMessage: 'End'
                                          })}
                                      </div>
                                  </div>}
                                  element={<Flag className={classes.infoIcon}/>}/>
            },
            // Trailing period
            {
                value: sceneEnd + trailingPeriod,
                label: <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                                  hintText={<div className={classes.tooltipColumn}>
                                      <div>{moment((sceneEnd + trailingPeriod) * 1000).format("mm:ss:SS")}</div>
                                      <div>
                                          {intl.formatMessage({
                                              id: 'app.scoredScene.edit.videoSlider.decisionLimit.label',
                                              defaultMessage: 'Decision Limit'
                                          })}
                                      </div>
                                  </div>}
                                  element={<SportsScore className={classes.infoIcon}/>}/>
            },
            // Scores
            ...defs.map((s, index) => {
                return {
                    value: s.time,
                    label: getScoreLabel(s, index)
                }
            })
        ]

        if (isBlurringWithinFence()) {
            marks.push(
                // Blurring
                {
                    value: blurringStart,
                    label: <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                                      hintText={<div className={classes.tooltipColumn}>
                                          <div>{moment(blurringStart * 1000).format("mm:ss:SS")}</div>
                                          <div>
                                              {intl.formatMessage({
                                                  id: 'app.scoredScene.edit.videoSlider.blurStart.label',
                                                  defaultMessage: 'Blur Start'
                                              })}
                                          </div>
                                      </div>}
                                      element={<BlurOn className={classes.infoIcon}/>}/>
                },
                {
                    value: blurringEnd,
                    label: <BasicHint options={{trigger: "hover", placement: 'bottom'}}
                                      hintText={<div className={classes.tooltipColumn}>
                                          <div>{moment(blurringEnd * 1000).format("mm:ss:SS")}</div>
                                          <div>
                                              {intl.formatMessage({
                                                  id: 'app.scoredScene.edit.videoSlider.blurEnd.label',
                                                  defaultMessage: 'Blur End'
                                              })}
                                          </div>
                                      </div>}
                                      element={<BlurOff className={classes.infoIcon}/>}/>
                })
        }

        // Extend marks in case of duplicates (otherwise they will be overwritten and only one of them will be displayed)
        const extendedMarks = []
        marks.forEach((mark) => {
            const alreadyIncludedMark = extendedMarks.find((m) => m.value === mark.value)

            if (!alreadyIncludedMark) {
                extendedMarks.push(mark)
            } else {
                alreadyIncludedMark.label = <div>
                    <div>{alreadyIncludedMark.label}</div>
                    <div style={{marginTop: 15}}>{mark.label}</div>
                </div>
            }
        })

        return extendedMarks;
    }

    const getScoreLabel = (scoreDefinition, index) => {
        return <BasicHint options={{trigger: "hover", placement: index % 2 === 1 ? 'bottom' : 'top'}}
                          hintText={<div className={classes.tooltipColumn}>
                              <div>
                                  <b>{moment(scoreDefinition.time * 1000).format("mm:ss:SS")}</b>
                              </div>
                              <div>
                                  {scoreDefinition.technique
                                      ? (intl.formatMessage({
                                          id: `app.ids.technique.${scoreDefinition.technique}`,
                                          defaultMessage: scoreDefinition.technique
                                      }) + (scoreDefinition.techniqueFreeText ? `: ${scoreDefinition.techniqueFreeText}` : ''))
                                      : ''}
                              </div>
                          </div>}
                          element={
                              <div
                                  className={getMarkClass(scoreDefinition.athlete, index)}>
                                  {scoreDefinition.score}
                              </div>
                          }/>
    }

    const getMarkClass = (athlete, index) => {
        if (athlete === 'blue') {
            if (index % 2 === 0) {
                return classes.roundScoreBlueTop
            } else {
                return classes.roundScoreBlueBottom
            }
        } else {
            if (index % 2 === 0) {
                return classes.roundScoreRedTop
            } else {
                return classes.roundScoreRedBottom
            }
        }
    }


    return <VideoProgressSlider onChange={onChange}
                                value={value}
                                valueLabelDisplay="off"
                                valueLabelFormat={(value, index) => ''}
                                min={sceneStart}
                                max={sceneEnd + trailingPeriod}
                                marks={getMarks()}
                                step={0.1}
                                disabled={disabled}
    />
}
export default ScoreEnrichedVideoProgressSlider
